/**
 * The three switches on the root element (UX spec v3 §10.1): `data-theme`,
 * `data-type` and `data-motion`. Everything in the Vault system keys on them.
 *
 * - **Theme** follows the device (`prefers-color-scheme`) and keeps following it.
 *   There is no in-app toggle until Me is built (run 3).
 * - **Type** is the customer's profile: the persona picks it at sign-in and the
 *   server uses the same profile to choose the payload's lifetime.
 * - **Motion** mirrors `prefers-reduced-motion` onto `html[data-motion]`, which
 *   is the attribute the system's reduced-motion rules key on.
 *
 * `?theme=dark|light`, `?type=large` and `?motion=reduced` exist for the camera
 * and the e2e fit matrix. They change appearance only, are the only query
 * parameters this app reads, and are documented in the README as demo tooling.
 */
export type Theme = "dark" | "light";
export type TypeSize = "normal" | "large";
export type Motion = "normal" | "reduced";

interface MediaLike {
  readonly matches: boolean;
  addEventListener?(type: "change", listener: () => void): void;
}
type MatchMedia = (query: string) => MediaLike;

export interface RootEnv {
  readonly root: HTMLElement;
  readonly search: string;
  readonly matchMedia: MatchMedia;
}

function param(search: string, key: string): string | null {
  return new URLSearchParams(search).get(key);
}

export function forcedTheme(search: string): Theme | undefined {
  const t = param(search, "theme");
  return t === "dark" || t === "light" ? t : undefined;
}

export function forcedType(search: string): TypeSize | undefined {
  return param(search, "type") === "large" ? "large" : undefined;
}

export function forcedMotion(search: string): Motion | undefined {
  return param(search, "motion") === "reduced" ? "reduced" : undefined;
}

/** Paint theme and motion now and keep them in step with the device. */
export function applyRoot(env: RootEnv): void {
  const dark = env.matchMedia("(prefers-color-scheme: dark)");
  const reduce = env.matchMedia("(prefers-reduced-motion: reduce)");
  const paint = (): void => {
    const theme: Theme = forcedTheme(env.search) ?? (dark.matches ? "dark" : "light");
    const motion: Motion = forcedMotion(env.search) ?? (reduce.matches ? "reduced" : "normal");
    env.root.setAttribute("data-theme", theme);
    env.root.setAttribute("data-motion", motion);
  };
  paint();
  dark.addEventListener?.("change", paint);
  reduce.addEventListener?.("change", paint);
  setType(env, "normal");
}

/** The customer's profile, unless the demo rig forces large type. */
export function setType(env: Pick<RootEnv, "root" | "search">, type: TypeSize): void {
  env.root.setAttribute("data-type", forcedType(env.search) ?? type);
}

export function reducedMotion(root: HTMLElement): boolean {
  return root.getAttribute("data-motion") === "reduced";
}
